import type { LensCard, LensCardData, LensCardView } from '~/types/leLens';

/**
 * Duplicate means the same answer rendered the same way, not simply two cards
 * that happen to share a dataset. A table and a chart can intentionally read
 * one dataset; two metric cards with the same reduction cannot.
 */
function stableValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableValue).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableValue(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function sourceFor(card: LensCard): string | null {
  return card.datasetId ? `dataset:${card.datasetId}` : null;
}

function keyFor(card: LensCard, data: LensCardData | undefined): string | null {
  if (data?.status !== 'ready') return null;
  const source = sourceFor(card);
  if (!source) return null;

  const view: LensCardView = card.view ?? {};
  return stableValue({ source, type: card.type, view, period: card.period ?? null });
}

/** Maps each later duplicate to the title of the first matching card. */
export function duplicateCardTitles(
  cards: Array<LensCard>,
  dataByCardId: ReadonlyMap<string, LensCardData>,
): ReadonlyMap<string, string> {
  const firstTitleByKey = new Map<string, string>();
  const duplicateOf = new Map<string, string>();

  for (const card of cards) {
    const key = keyFor(card, dataByCardId.get(card.id));
    if (!key) continue;
    const firstTitle = firstTitleByKey.get(key);
    if (firstTitle) duplicateOf.set(card.id, firstTitle);
    else firstTitleByKey.set(key, card.title);
  }

  return duplicateOf;
}
